import { useEffect, useState } from 'react';
import { CheckNever, formatDate, MakeAxiosRequest } from '../../core/utils';
import APIUrls from '../../core/ApiUrls';

enum EAlertPriority {
    LOW = 'LOW',
    MEDIUM = 'MEDIUM',
    HIGH = 'HIGH',
}
interface IAlert {
    id: string;
    title: string;
    description: string;
    priority: EAlertPriority;
    created_at: Date;
}

interface IAlertWithLead extends IAlert {
    leadName: string;
    linkedinUrl: string;
}
interface IResponse {
    data: {
        alerts: IAlertWithLead[];
        pagination: {
            page: number;
            limit: number;
            count: number;
            total_pages: number;
            has_more: boolean;
        };
    };
}

const Alert = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [alerts, setAlerts] = useState<IAlertWithLead[]>([]);
    const [page, setPage] = useState<number>(1);
    const [limit, setLimit] = useState<number>(10);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [filter, setFilter] = useState<EAlertPriority | 'NONE'>('NONE');

    const getPriorityColour = (priority: EAlertPriority) => {
        switch (priority) {
            case EAlertPriority.LOW:
                return {
                    primary: '#22C55E',
                    secondary: '#F0FDF4',
                };
            case EAlertPriority.MEDIUM:
                return {
                    primary: '#EAB308',
                    secondary: '#FEFCE8',
                };
            case EAlertPriority.HIGH:
                return {
                    primary: '#EF4444',
                    secondary: '#FEF2F2',
                };
            default:
                return CheckNever(priority);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams();
                params.append('page', page.toString());
                params.append('limit', limit.toString());
                if (filter !== 'NONE') {
                    params.append('priority', filter);
                }

                const res = await MakeAxiosRequest<IResponse>('GET', `${APIUrls.getURL('ALERTS')}?${params.toString()}`);
                const responseData = res?.data?.data;
                setAlerts(responseData?.alerts || []);
                setTotalCount(responseData?.pagination?.count || 0);
                setTotalPages(responseData?.pagination?.total_pages || 1);
            } catch (error) {
                console.log(error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [page, limit, filter]);
    return (
        <div className="w-full overflow-x-auto">
            <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h1 className="text-2xl font-bold text-gray-800">Alerts</h1>
                <div className="relative flex flex-row items-center">
                    <label htmlFor="priority-filter" className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                        Priority Filter
                    </label>
                    <select
                        id="priority-filter"
                        name="priority"
                        value={filter}
                        onChange={e => setFilter(e.target.value as EAlertPriority | 'NONE')}
                        className="appearance-none my-2 mx-2 px-4 py-2 pr-10 rounded-lg border border-gray-300 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors shadow-sm text-sm font-medium"
                    >
                        <option label="None" value="NONE"></option>
                        <option label="Low Priority" value={EAlertPriority.LOW}></option>
                        <option label="Medium Priority" value={EAlertPriority.MEDIUM}></option>
                        <option label="High Priority" value={EAlertPriority.HIGH}></option>
                    </select>
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                    </span>
                </div>
            </div>
            {loading && (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin h-10 w-10 border-4 border-purple-500 border-t-transparent rounded-full"></div>
                </div>
            )}
            {!loading && alerts.length === 0 && (
                <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <div className="text-gray-400 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Alerts Currently</h3>
                    <p className="text-gray-500 mb-6">Changes in leads profiles with show up here.</p>
                </div>
            )}
            {!loading && alerts.length !== 0 && (
                <div className="bg-white border-collapse border-[1px] border-gray-300 rounded-xl shadow-sm overflow-x-hidden overflow-y-auto max-h-[600px]">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="border-b border-gray-200 bg-white">
                                <tr>
                                    <th className="px-4 py-4 text-xs text-left font-semibold text-gray-700 uppercase EAlertPriority.tracking-wider">Lead</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase EAlertPriority.tracking-wider">Title</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase EAlertPriority.tracking-wider">Description</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase EAlertPriority.tracking-wider">Priority</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase EAlertPriority.tracking-wider">Created At</th>
                                </tr>
                            </thead>
                            <tbody>
                                {alerts.map(alert => {
                                    const primary = getPriorityColour(alert.priority)?.primary;
                                    const secondary = getPriorityColour(alert.priority)?.secondary;
                                    return (
                                        <tr key={alert.id} style={{ backgroundColor: secondary }} className="border-b-[1px] border-gray-200">
                                            <td className="px-4 py-4">
                                                <div className="truncate max-w-[180px]">
                                                    <a href={alert.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                                                        {alert.leadName}
                                                    </a>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 w-fit">
                                                <div className="text-sm font-semibold text-gray-900 max-w-[180px] truncate">{alert.title}</div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="text-sm font-semibold text-gray-900 max-w-[500px] whitespace-normal overflow-hidden">{alert.description}</div>
                                            </td>
                                            <td>
                                                <div style={{ borderColor: primary, color: primary }} className="px-3 py-2 border-[1px] rounded-3xl text-xs font-semibold text-gray-900 w-fit">
                                                    {alert.priority}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-500">{formatDate(alert.created_at)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            {!loading && alerts.length > 0 && (
                <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4 bg-white p-4 rounded-xl shadow-sm">
                    <div className="text-sm text-gray-700">
                        Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to <span className="font-medium">{Math.min(page * limit, totalCount)}</span> of <span className="font-medium">{totalCount}</span> alerts
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                            Previous
                        </button>
                        <div className="flex items-center gap-1">
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(p => {
                                    // Show first page, last page, current page, and pages around current
                                    return p === 1 || p === totalPages || Math.abs(p - page) <= 1;
                                })
                                .map((p, idx, arr) => (
                                    <>
                                        {idx > 0 && arr[idx - 1] !== p - 1 && (
                                            <span key={`ellipsis-${p}`} className="px-2 text-gray-500">
                                                ...
                                            </span>
                                        )}
                                        <button key={p} onClick={() => setPage(p)} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${page === p ? 'bg-purple-600 text-white' : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'}`}>
                                            {p}
                                        </button>
                                    </>
                                ))}
                        </div>
                        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                            Next
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <label htmlFor="limit" className="text-sm text-gray-700">
                            Items per page:
                        </label>
                        <select
                            id="limit"
                            value={limit}
                            onChange={e => {
                                setLimit(Number(e.target.value));
                                setPage(1);
                            }}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500">
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </select>
                    </div>
                </div>
            )}
        </div>
    );
};
export default Alert;
